import { object, objectHasKeys } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { type ApiOrgProduct, getScanInlineProductFromOrgProduct } from '@kaiizen/kai-pro-api';
import {
  getEffectiveScanValue,
  getResolvedContractorId,
  getTaskOptimizedPreview,
  getUpdatedScanValue,
  isOtherTask,
  ScanOptionOptimized,
  ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';
import { useState } from 'react';

import { useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import { getTaskTradeForContractorAssignment } from '@/Client/Lib/contractorTradeLib';
import { ScanDataMutators, useScanEditingContextSelector, useScanEditingContextSlice } from '@/Client/State';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { ExpandCaret, LabeledInput } from '@/Client/Widgets';

import { FocusedInlineOptionEditor } from './FocusedInlineOptionEditor';
import {
  FOCUSED_MODULE_BOX_STYLE,
  FOCUSED_MODULE_HEADER_STYLE,
  FOCUSED_MODULE_TITLE_STYLE,
} from './focusedModuleStyles';
import { FocusedScanValueView } from './FocusedScanValueView';
import { FocusedSimpleContractorPicker } from './FocusedSimpleContractorPicker';
import {
  FOCUSED_TASK_CARD_LABEL,
  FOCUSED_TASK_CARD_OPTIONS,
  FOCUSED_TASK_CARD_SECTION,
  FOCUSED_TASK_CARD_VALUE_FULL_WIDTH,
  FOCUSED_TASK_CARD_VALUES,
  FOCUSED_TASK_DETAILS_BODY_STYLE,
  FOCUSED_TASK_DETAILS_HEADER_STYLE,
} from './focusedTaskCardStyles';
import { TaskDescription } from './TaskDescription';

// Single source of truth for this section's own user-facing copy — referenced by
// tests instead of duplicating the literal string.
export const TASK_DETAILS_TITLE = 'Task Details';

/**
 * The "Task Details" accordion — contractor and description, side by side (prototype
 * `.sec-taskdesc`). Collapsed by default once the scope is approved (and beyond, through
 * closeout): the contractor is settled by then and the description is reference material.
 * Starts expanded regardless of stage when the contractor is unassigned — that's the field
 * needing attention, and folding it away would hide the reason the task got flagged in the
 * first place.
 *
 * This default is only computed once per task, on the first render that lands on it — not
 * re-derived on every render. Re-deriving live would collapse the section the instant a user
 * picks a contractor for a previously-unassigned task, fighting the very edit that opened it.
 * From then on, only the user's own toggle changes it, until they switch to a different task,
 * which re-seeds the default fresh.
 */
const FocusedTaskDetails = ({
  baselineTask,
  task,
  groupId,
}: {
  baselineTask?: ScanTaskOptimized;
  task: ScanTaskOptimized;
  groupId: string;
}) => {
  const { scanIsEditable, scanDataMutators, defaultContractorsByTrade } = useScanEditingContextSlice([
    'scanIsEditable',
    'scanDataMutators',
    'defaultContractorsByTrade',
  ]);
  const projectStateLifecycle = useProjectStateLifecycle();
  const contractorTrade = getTaskTradeForContractorAssignment(task);
  const contractorIsUnassigned = !getResolvedContractorId(contractorTrade, task.assignedContractorId, defaultContractorsByTrade);

  const scopeIsSettled = projectStateLifecycle === 'approved' || projectStateLifecycle === 'closeout';

  // Seeded once per task, then left alone — re-deriving this on every render would collapse
  // the section the instant a user picks a contractor for a previously-unassigned task,
  // fighting the very edit that was the reason it opened. Only switching to a different task
  // (or the user's own toggle) should change it again.
  const [expandedState, setExpandedState] = useState({ taskId: task.id, expanded: contractorIsUnassigned || !scopeIsSettled });
  if (expandedState.taskId !== task.id) {
    setExpandedState({ taskId: task.id, expanded: contractorIsUnassigned || !scopeIsSettled });
  }
  const expanded = expandedState.expanded;

  const taskDescriptionValue = task.values.taskDescription;
  const taskDescriptionTemplate = task.template.values?.taskDescription;
  const taskDescriptionText = taskDescriptionValue != null
    ? String(getEffectiveScanValue(taskDescriptionValue) ?? '')
    : '';

  return (
    <div css={FOCUSED_TASK_CARD_SECTION} data-testid={'focused-task-details'}>
      <button
        type={'button'}
        css={FOCUSED_TASK_DETAILS_HEADER_STYLE}
        aria-expanded={expanded}
        onClick={() => setExpandedState({ taskId: task.id, expanded: !expanded })}
      >
        <span css={FOCUSED_MODULE_TITLE_STYLE}>{TASK_DETAILS_TITLE}</span>
        <ExpandCaret expanded={expanded} initialRotation={90} finalRotation={180} />
      </button>
      {expanded && (
        <div css={FOCUSED_TASK_DETAILS_BODY_STYLE}>
          <FocusedSimpleContractorPicker
            backgroundColor={'var(--background)'}
            assignedContractorId={task.assignedContractorId || ''}
            baselineAssignedContractorId={baselineTask?.assignedContractorId || ''}
            updateAssignedContractorId={contractorId => {
              if (!scanIsEditable) return;
              scanDataMutators.updateTask(groupId, task.id, { assignedContractorId: contractorId });
            }}
            trade={contractorTrade}
            filterByTrade={false}
            labelOverride={'Contractor'}
            disabled={!scanIsEditable}
            size={'medium'}
          />
          {isOtherTask(task) && taskDescriptionTemplate && taskDescriptionValue
            ? (
              <div css={FOCUSED_TASK_CARD_SECTION}>
                <span css={FOCUSED_TASK_CARD_LABEL}>{'Task description'}</span>
                <LabeledInput
                  value={taskDescriptionText}
                  disabled={!scanIsEditable}
                  multiline
                  rows={3}
                  size={'medium'}
                  width={'100%'}
                  backgroundColor={'var(--background)'}
                  onUpdate={value => {
                    if (!scanIsEditable) return;
                    const trimmed = value.trim();
                    scanDataMutators.updateTask(groupId, task.id, {
                      values: {
                        taskDescription: getUpdatedScanValue(taskDescriptionTemplate, taskDescriptionValue, trimmed),
                      },
                    });
                  }}
                />
              </div>
            )
            : <TaskDescription description={task.template.description} />}
        </div>
      )}
    </div>
  );
};

/** The Options section title runs one size up from the standard module title. */
const OPTIONS_TITLE_STYLE = css`
  label: OPTIONS_TITLE_STYLE;
  font-size: ${pxToRem(11)};
`;

/**
 * The task's own fields — whatever values its template declares, then the "Task Details"
 * accordion (contractor + description) folded behind its label, per the prototype's task header.
 *
 * Split from the options below it because the two belong to different sections of the panel now:
 * these read as the task's overview, the way a group's aggregates do, while options are a section
 * of their own. See FocusedTaskOptionsSection.
 */
export const FocusedTaskFields = ({
  baselineTask,
  task,
  groupId,
}: {
  baselineTask?: ScanTaskOptimized;
  task: ScanTaskOptimized;
  groupId: string;
}) => {
  const { scanIsEditable, scanDataMutators } = useScanEditingContextSlice(['scanIsEditable', 'scanDataMutators']);

  return (
    <>
      {(objectHasKeys(task.template.values) || isOtherTask(task)) && (
        <div css={FOCUSED_TASK_CARD_VALUES}>
          {isOtherTask(task) && (
            <div css={[FOCUSED_TASK_CARD_SECTION, FOCUSED_TASK_CARD_VALUE_FULL_WIDTH]}>
              <span css={FOCUSED_TASK_CARD_LABEL}>{'Non-standard task label'}</span>
              <LabeledInput
                value={task.customLabel ?? ''}
                disabled={!scanIsEditable}
                size={'medium'}
                width={'100%'}
                backgroundColor={'var(--background)'}
                onUpdate={value => {
                  if (!scanIsEditable) return;
                  scanDataMutators.updateTask(groupId, task.id, { customLabel: value });
                }}
              />
            </div>
          )}
          {Object.entries(task.template.values || {})
            .filter(([key]) => !(isOtherTask(task) && key === 'taskDescription'))
            .map(([key, value]) => (
              <FocusedScanValueView
                key={key}
                size={'medium'}
                valueKey={key}
                value={task.values[key]}
                templateValue={task.template.values![key]}
                updateValue={(key, newValue) => {
                  if (!scanIsEditable) return;
                  scanDataMutators.updateTask(groupId, task.id, { values: { [key]: newValue } });
                }}
              />
            ))}
        </div>
      )}
      <FocusedTaskDetails baselineTask={baselineTask} task={task} groupId={groupId} />
    </>
  );
};

/**
 * A preview option gets a new id each time the task's previews are rebuilt (when the task or its products change), so its
 * card is keyed by its template and stays mounted, the way an option in the scan stays keyed by its id.
 */
const getOptionCardKey = (option: ScanOptionOptimized, existingOptionTemplateIds: Set<string>) =>
  existingOptionTemplateIds.has(option.templateId) ? option.id : option.templateId;

/**
 * The task's options and the line items inside them — a section of the panel in its own right,
 * titled like Photos and Notes are, rather than a block inside the task's card.
 */
export const FocusedTaskEditor = ({
  taskId,
  groupId,
}: {
  taskId: string;
  groupId: string;
}) => {
  const { scanDataMutators, taskWithPreviewOptions, existingOptionTemplateIds } = useScanEditingContextSelector<
    {
      scanDataMutators: ScanDataMutators;
      task: ScanTaskOptimized | undefined;
      orgProductsById: Record<string, ApiOrgProduct> | undefined;
      taskWithPreviewOptions: ScanTaskOptimized | undefined;
      existingOptionTemplateIds: Set<string>;
    }
  >(({ scanRoot, scanDataMutators, orgProductsById }, prevState) => {
    const group = scanRoot?.groupsById[groupId];
    const task = group?.tasksById[taskId];
    if (prevState && prevState.task === task && prevState.orgProductsById === orgProductsById) return prevState;
    const existingOptionTemplateIds: Set<string> = new Set(task?.options.map(option => option.templateId) ?? []);
    // Previews cost what the real thing will: a SKU'd line item is seeded with its product's price, exactly as on add.
    const inlineProductsById = orgProductsById && object(orgProductsById, getScanInlineProductFromOrgProduct);
    const taskWithPreviewOptions: ScanTaskOptimized | undefined = group && task ? getTaskOptimizedPreview(group, task, Date.now(), inlineProductsById) : task;
    return { scanDataMutators, task, orgProductsById, taskWithPreviewOptions, existingOptionTemplateIds };
  }, [groupId, taskId]);
  if (!taskWithPreviewOptions || !groupId) return null;

  return (
    /* The same white box Notes sits in — the design's standard module container. */
    <div css={FOCUSED_MODULE_BOX_STYLE} data-testid={'focused-task-options'}>
      <div css={FOCUSED_MODULE_HEADER_STYLE}>
        <h3 css={[FOCUSED_MODULE_TITLE_STYLE, OPTIONS_TITLE_STYLE]}>{'Options'}</h3>
      </div>
      <div css={FOCUSED_TASK_CARD_OPTIONS}>
        {taskWithPreviewOptions.options.map(scanOption => (
          <FocusedInlineOptionEditor
            key={getOptionCardKey(scanOption, existingOptionTemplateIds)}
            groupId={groupId}
            option={scanOption}
            optionTemplate={scanOption.template}
            scanDataMutators={scanDataMutators}
            taskId={taskWithPreviewOptions.id}
            preview={!existingOptionTemplateIds.has(scanOption.templateId)}
          />
        ))}
      </div>
    </div>
  );
};
